import { useCallback, useState } from 'react';

import { useHovering } from 'mastodon/hooks/useHovering';
import { autoPlayGif } from 'mastodon/initial_state';
import type { Account, AccountShapeFull } from 'mastodon/models/account';
import { getAvatarInitial, isDefaultAvatarUrl } from 'mastodon/utils/avatar';

type AvatarAccount = Pick<
  Account | AccountShapeFull,
  'acct' | 'avatar' | 'avatar_static'
>;

interface Props {
  account?: AvatarAccount;
  friend?: AvatarAccount;
  size?: number;
  baseSize?: number;
  overlaySize?: number;
}

const AvatarOverlayImage: React.FC<{
  account?: AvatarAccount;
  src?: string;
  size: number;
}> = ({ account, src, size }) => {
  const [error, setError] = useState(false);
  const handleError = useCallback(() => {
    setError(true);
  }, []);
  const showInitial = error || isDefaultAvatarUrl(src);

  return (
    <div className='account__avatar' style={{ width: size, height: size }}>
      {showInitial ? (
        <span
          className='account__avatar__initial'
          aria-hidden='true'
          style={{ fontSize: `${Math.max(size * 0.5, 10)}px` }}
        >
          {getAvatarInitial(account?.acct)}
        </span>
      ) : (
        src && (
          <img src={src} alt={account?.acct} onError={handleError} />
        )
      )}
    </div>
  );
};

export const AvatarOverlay: React.FC<Props> = ({
  account,
  friend,
  size = 46,
  baseSize = 36,
  overlaySize = 24,
}) => {
  const { hovering, handleMouseEnter, handleMouseLeave } =
    useHovering(autoPlayGif);
  const accountSrc = hovering ? account?.avatar : account?.avatar_static;
  const friendSrc = hovering ? friend?.avatar : friend?.avatar_static;

  return (
    <div
      className='account__avatar-overlay'
      style={{ width: size, height: size }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className='account__avatar-overlay-base'>
        <AvatarOverlayImage account={account} src={accountSrc} size={baseSize} />
      </div>
      <div className='account__avatar-overlay-overlay'>
        <AvatarOverlayImage account={friend} src={friendSrc} size={overlaySize} />
      </div>
    </div>
  );
};
